import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Users, Wifi, Coffee, MapPin, Star, Tv, Wind, UtensilsCrossed, BedDouble, Refrigerator } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/format';
import { ShoppingCart } from 'lucide-react';
import MobileMenu from '@/components/MobileMenu';
import Footer from '@/components/Footer';

const LandingPage = () => {
  const navigate = useNavigate();
  
  // Helper function to parse URL parameters
  const getUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      checkIn: params.get('checkIn'),
      checkOut: params.get('checkOut'),
      adults: params.get('adults'),
      rooms: params.get('rooms')
    };
  };
  
  const urlParams = getUrlParams();
  
  const [checkIn, setCheckIn] = useState(() => {
    // Check if checkIn is provided in URL
    if (urlParams.checkIn) {
      const date = new Date(urlParams.checkIn);
      if (!isNaN(date.getTime())) {
        date.setHours(0, 0, 0, 0);
        return date;
      }
    }
    // Default: tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
  });
  
  const [checkOut, setCheckOut] = useState(() => {
    // Check if checkOut is provided in URL
    if (urlParams.checkOut) {
      const date = new Date(urlParams.checkOut);
      if (!isNaN(date.getTime())) {
        date.setHours(0, 0, 0, 0);
        return date;
      }
    }
    // Default: 3 nights from check-in
    const inFourDays = new Date();
    inFourDays.setDate(inFourDays.getDate() + 4);
    inFourDays.setHours(0, 0, 0, 0);
    return inFourDays;
  });
  
  const [numGuests, setNumGuests] = useState(() => {
    // Check if adults is provided in URL
    if (urlParams.adults) {
      const adults = parseInt(urlParams.adults);
      if (!isNaN(adults) && adults > 0 && adults <= 20) {
        return adults;
      }
    }
    return 2; // Default
  });
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
  const [settings, setSettings] = useState({ logo_url: null, hotel_name: 'Hotel Kaiser' });
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [datesWithRates, setDatesWithRates] = useState(new Set());
  const [loadingRates, setLoadingRates] = useState(true);
  const [minStayRules, setMinStayRules] = useState({});
  
  // Popover open state for auto-advance
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [mobileCheckInOpen, setMobileCheckInOpen] = useState(false);
  const [mobileCheckOutOpen, setMobileCheckOutOpen] = useState(false);

  // Fetch settings (logo and hotel name)
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/payment-settings`);
      if (response.data) {
        setSettings({
          logo_url: response.data.logo_url || null,
          hotel_name: response.data.hotel_name || 'Hotel Kaiser'
        });
        console.log('✅ Branding loaded:', {
          logo: response.data.logo_url ? 'Yes' : 'No',
          name: response.data.hotel_name
        });
      }
    } catch (error) {
      console.log('⚠️ Settings not available:', error);
    }
  };

  // Load cart count, available dates, min-stay rules, and settings on mount
  React.useEffect(() => {
    updateCartCount();
    fetchAvailableDates();
    fetchSettings();
    fetchMinStayRules();
  }, []);
  
  // Auto-search trigger state
  const [autoSearchTriggered, setAutoSearchTriggered] = React.useState(false);

  const fetchAvailableDates = async () => {
    try {
      setLoadingRates(true);
      // Fetch available dates from public endpoint (no auth required)
      const response = await axios.get(`${API}/public/available-dates`);
      const dates = response.data.dates || [];
      
      // Create a Set of dates that have rates
      const datesSet = new Set(dates);
      
      setDatesWithRates(datesSet);
    } catch (error) {
      console.error('Error fetching available dates:', error);
      // If rates can't be loaded, allow all dates (fallback)
      toast.error('Could not load available dates');
    } finally {
      setLoadingRates(false);
    }
  };

  const fetchMinStayRules = async () => {
    try {
      const response = await axios.get(`${API}/public/min-stay-rules`);
      setMinStayRules(response.data.rules || {});
    } catch (error) {
      console.error('Error fetching min-stay rules:', error);
    }
  };

  // Helper: get the minimum stay for a given check-in date
  // On the landing page (before room type selection), use the LOWEST min stay
  // across all room types so the search allows the widest range.
  // Per-room-type enforcement happens at booking time.
  const getMinStayForDate = (dateStr) => {
    let minAcross = null;
    for (const rtRules of Object.values(minStayRules)) {
      const ms = rtRules[dateStr];
      if (ms) {
        if (minAcross === null || ms < minAcross) minAcross = ms;
      }
    }
    return minAcross || 1;
  };

  const updateCartCount = () => {
    const savedCart = localStorage.getItem('bookingCart');
    if (savedCart) {
      const cart = JSON.parse(savedCart);
      const count = cart.reduce((sum, item) => sum + item.quantity, 0);
      setCartCount(count);
    }
  };

  const addToCart = (roomType) => {
    // Save dates
    localStorage.setItem('bookingCheckIn', format(checkIn, 'yyyy-MM-dd'));
    localStorage.setItem('bookingCheckOut', format(checkOut, 'yyyy-MM-dd'));
    
    // Load existing cart
    const savedCart = localStorage.getItem('bookingCart');
    const cart = savedCart ? JSON.parse(savedCart) : [];
    
    // Check if room type already in cart
    const existingIndex = cart.findIndex(item => item.room_type_id === roomType.room_type_id);
    
    if (existingIndex >= 0) {
      // Increase quantity
      if (cart[existingIndex].quantity < roomType.available_count) {
        cart[existingIndex].quantity += 1;
        toast.success(`Added another ${roomType.name}`);
      } else {
        toast.error(`Maximum ${roomType.available_count} rooms available`);
        return;
      }
    } else {
      // Add new item with quantity 1
      cart.push({ ...roomType, quantity: 1 });
      toast.success(`${roomType.name} added to cart`);
    }
    
    // Save cart
    localStorage.setItem('bookingCart', JSON.stringify(cart));
    updateCartCount();
  };

  const searchAvailability = async () => {
    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/room-types/available`, {
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd')
      });
      setAvailableRoomTypes(response.data);
      setShowResults(true);
      if (response.data.length === 0) {
        toast.info('No room types available for selected dates');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to search rooms');
    } finally {
      setLoading(false);
    }
  };

  // Auto-search when URL parameters are present
  React.useEffect(() => {
    if (urlParams.checkIn && urlParams.checkOut && !autoSearchTriggered) {
      setAutoSearchTriggered(true);
      // Wait a bit for dates to be set, then trigger search
      const timer = setTimeout(() => {
        searchAvailability();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [checkIn, checkOut, autoSearchTriggered]);

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Mobile Menu */}
      <MobileMenu />

      {/* Header - Mobile Optimized */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm border-b px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            {settings.logo_url ? (
              <img 
                src={settings.logo_url} 
                alt={settings.hotel_name} 
                className="h-10 sm:h-12 w-auto object-contain"
                onError={(e) => {
                  // Fallback to icon if logo fails to load
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className={settings.logo_url ? 'hidden' : 'w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center'}>
              <MapPin className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {settings.hotel_name || 'Hotel Kaiser'}
              </h1>
              <p className="text-xs sm:text-sm text-gray-600">Corfu, Greece</p>
            </div>
          </div>
          
          {/* Desktop Navigation - Hidden on Mobile */}
          <div className="hidden lg:flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/guest/login')}>
              Guest Portal
            </Button>
            {/* DISABLED: Shopping cart feature - keeping code for future use
            <Button
              variant="outline"
              onClick={() => navigate('/cart')}
              className="relative"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Button>
            */}
            {/* DISABLED: Admin Login button from public page - keeping code for future use
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin/login')}
              data-testid="admin-login-btn"
            >
              Admin Login
            </Button>
            */}
          </div>
        </div>
      </header>

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto">
        {/* Sticky Search Bar for Mobile */}
        <div className="md:hidden sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-md px-3 py-3 border-b">
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
              <Popover open={mobileCheckInOpen} onOpenChange={setMobileCheckInOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-14 text-sm"
                    data-testid="mobile-check-in-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{checkIn ? format(checkIn, 'EEE, MMM d') : 'Check-in'}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={checkIn}
                    onSelect={(date) => {
                      setCheckIn(date);
                      if (date && checkOut) {
                        const ciStr = format(date, 'yyyy-MM-dd');
                        const minNights = getMinStayForDate(ciStr);
                        const minCheckOut = new Date(date);
                        minCheckOut.setDate(minCheckOut.getDate() + minNights);
                        if (checkOut < minCheckOut) {
                          setCheckOut(minCheckOut);
                        }
                      }
                      // Auto-advance: close check-in, open check-out
                      setMobileCheckInOpen(false);
                      setTimeout(() => setMobileCheckOutOpen(true), 150);
                    }}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      if (date < today) return true;
                      const dateStr = format(date, 'yyyy-MM-dd');
                      return !datesWithRates.has(dateStr);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              
              <Popover open={mobileCheckOutOpen} onOpenChange={setMobileCheckOutOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-14 text-sm"
                    data-testid="mobile-check-out-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{checkOut ? format(checkOut, 'EEE, MMM d') : 'Check-out'}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={checkOut}
                    onSelect={(date) => {
                      setCheckOut(date);
                      setMobileCheckOutOpen(false);
                    }}
                    defaultMonth={checkIn || new Date()}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      if (date < today) return true;
                      if (checkIn) {
                        const ciStr = format(checkIn, 'yyyy-MM-dd');
                        const minNights = getMinStayForDate(ciStr);
                        const minCheckOut = new Date(checkIn);
                        minCheckOut.setDate(minCheckOut.getDate() + minNights);
                        minCheckOut.setHours(0, 0, 0, 0);
                        if (date < minCheckOut) return true;
                        const dateStr = format(date, 'yyyy-MM-dd');
                        return !datesWithRates.has(dateStr);
                      }
                      return false;
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Mobile Min Stay Info */}
            {checkIn && (() => {
              const ciStr = format(checkIn, 'yyyy-MM-dd');
              const minNights = getMinStayForDate(ciStr);
              return minNights > 1 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="mobile-min-stay-notice">
                  <p className="text-xs text-amber-800 font-medium">
                    Minimum stay: {minNights} nights
                  </p>
                </div>
              ) : null;
            })()}
            
            <div className="flex gap-2">
              <div className="flex flex-col flex-1">
                <span className="text-xs text-gray-500 mb-1">Guests <span className="text-blue-600">(+ 15)</span></span>
                <div className="flex items-center h-14 border border-gray-300 rounded-md overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setNumGuests(Math.max(1, numGuests - 1))}
                    className="h-full px-4 bg-gray-50 active:bg-gray-200 text-gray-600 font-bold text-xl transition-colors border-r border-gray-300"
                    aria-label="Decrease guests"
                  >
                    −
                  </button>
                  <div className="flex-1 flex items-center justify-center gap-1">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{numGuests}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNumGuests(Math.min(10, numGuests + 1))}
                    className="h-full px-4 bg-gray-50 active:bg-gray-200 text-gray-600 font-bold text-xl transition-colors border-l border-gray-300"
                    aria-label="Increase guests"
                  >
                    +
                  </button>
                </div>
              </div>
              
              <Button 
                onClick={searchAvailability} 
                className="h-14 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 active:from-blue-700 active:to-indigo-700 text-white font-semibold self-end"
                disabled={loading}
              >
                {loading ? '...' : 'Search'}
              </Button>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative py-8 sm:py-12 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.1),transparent_50%),radial-gradient(circle_at_70%_60%,rgba(99,102,241,0.1),transparent_50%)]" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Search Card - Hidden on Mobile (using sticky bar instead) */}
          <Card className="hidden md:block max-w-4xl mx-auto shadow-xl border-0 bg-white/90 backdrop-blur-sm" data-testid="search-card">
            <CardContent className="p-4 sm:p-6 lg:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Check-in</label>
                  <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal h-12"
                        data-testid="check-in-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {checkIn ? format(checkIn, 'EEE, MMM d, yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkIn}
                        onSelect={(date) => {
                          setCheckIn(date);
                          // Auto-adjust check-out based on min stay for selected date
                          if (date && checkOut) {
                            const ciStr = format(date, 'yyyy-MM-dd');
                            const minNights = getMinStayForDate(ciStr);
                            const minCheckOut = new Date(date);
                            minCheckOut.setDate(minCheckOut.getDate() + minNights);
                            if (checkOut < minCheckOut) {
                              setCheckOut(minCheckOut);
                            }
                          }
                          // Auto-advance: close check-in, open check-out
                          setCheckInOpen(false);
                          setTimeout(() => setCheckOutOpen(true), 150);
                        }}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          // Disable past dates
                          if (date < today) return true;
                          
                          // Disable dates without rates (no availability)
                          const dateStr = format(date, 'yyyy-MM-dd');
                          return !datesWithRates.has(dateStr);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Check-out</label>
                  <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal h-12"
                        data-testid="check-out-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {checkOut ? format(checkOut, 'EEE, MMM d, yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkOut}
                        onSelect={(date) => {
                          setCheckOut(date);
                          setCheckOutOpen(false);
                        }}
                        defaultMonth={checkIn || new Date()}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          // Disable past dates
                          if (date < today) return true;
                          
                          if (checkIn) {
                            // Dynamic minimum stay from Beds24
                            const ciStr = format(checkIn, 'yyyy-MM-dd');
                            const minNights = getMinStayForDate(ciStr);
                            const minCheckOut = new Date(checkIn);
                            minCheckOut.setDate(minCheckOut.getDate() + minNights);
                            minCheckOut.setHours(0, 0, 0, 0);
                            if (date < minCheckOut) return true;
                            
                            // Disable dates without rates (no availability)
                            const dateStr = format(date, 'yyyy-MM-dd');
                            return !datesWithRates.has(dateStr);
                          }
                          return false;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                {/* Availability Info */}
                {loadingRates ? (
                  <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">Loading available dates...</p>
                  </div>
                ) : datesWithRates.size === 0 ? (
                  <div className="col-span-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-700">
                      ⚠️ No rates available yet
                    </p>
                  </div>
                ) : null}

                {/* Min Stay Info */}
                {checkIn && (() => {
                  const ciStr = format(checkIn, 'yyyy-MM-dd');
                  const minNights = getMinStayForDate(ciStr);
                  return minNights > 1 ? (
                    <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="min-stay-notice">
                      <p className="text-sm text-amber-800 font-medium">
                        Minimum stay: {minNights} nights for selected check-in date
                      </p>
                    </div>
                  ) : null;
                })()}
                
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Number of Guests <span className="text-blue-600">(+ 15)</span></label>
                  <div className="flex items-center h-12 border border-gray-300 rounded-md overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setNumGuests(Math.max(1, numGuests - 1))}
                      className="h-full px-4 bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold text-lg transition-colors border-r border-gray-300"
                      aria-label="Decrease guests"
                    >
                      −
                    </button>
                    <div className="flex-1 flex items-center justify-center gap-2">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900" data-testid="num-guests-display">{numGuests}</span>
                      <span className="text-gray-500 text-sm">{numGuests === 1 ? 'guest' : 'guests'}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNumGuests(Math.min(10, numGuests + 1))}
                      className="h-full px-4 bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold text-lg transition-colors border-l border-gray-300"
                      aria-label="Increase guests"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="flex items-end">
                  <Button 
                    onClick={searchAvailability} 
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold"
                    disabled={loading}
                    data-testid="search-rooms-btn"
                  >
                    {loading ? 'Searching...' : 'Search Rooms'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Available Room Types */}
          {showResults && (
            <div className="mt-16" data-testid="search-results">
              <h3 className="text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Available Room Types
              </h3>
              
              {availableRoomTypes.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No rooms available for selected dates</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {availableRoomTypes.map((roomType) => (
                    <Card 
                      key={roomType.room_type_id} 
                      className="group hover:shadow-2xl transition-all duration-300 cursor-pointer border-0 overflow-hidden"
                      onClick={() => navigate(`/room-type/${roomType.room_type_id}`, { 
                        state: { 
                          checkIn: format(checkIn, 'yyyy-MM-dd'), 
                          checkOut: format(checkOut, 'yyyy-MM-dd'),
                          numGuests: numGuests,
                          roomType: roomType
                        } 
                      })}
                      data-testid={`room-type-card-${roomType.room_type_id}`}
                    >
                      <div className="aspect-video bg-gradient-to-br from-blue-100 to-indigo-100 relative overflow-hidden">
                        {roomType.image_url ? (
                          <img src={roomType.image_url} alt={roomType.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <MapPin className="w-16 h-16 text-blue-400" />
                          </div>
                        )}
                        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-green-600">
                          {roomType.available_count} available
                        </div>
                      </div>
                      <CardContent className="p-6">
                        <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{roomType.name}</h4>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{roomType.description}</p>
                        
                        <div className="flex flex-wrap gap-2 mb-4">
                          <div className="flex items-center text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            <Users className="w-4 h-4 mr-1" />
                            {roomType.max_guests || roomType.sample_room?.max_guests || 2} {(roomType.max_guests || roomType.sample_room?.max_guests || 2) === 1 ? 'guest' : 'guests'}
                          </div>
                          <div className="flex items-center text-sm text-green-700 bg-green-50 px-2 py-1 rounded">
                            <UtensilsCrossed className="w-4 h-4 mr-1" />
                            Breakfast Included
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                          <div className="flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                            <BedDouble className="w-3 h-3 mr-1" />
                            Queen Bed
                          </div>
                          <div className="flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                            <Wifi className="w-3 h-3 mr-1" />
                            Wi-Fi
                          </div>
                          <div className="flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                            <Tv className="w-3 h-3 mr-1" />
                            TV
                          </div>
                          <div className="flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                            <Wind className="w-3 h-3 mr-1" />
                            A/C
                          </div>
                          <div className="flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                            <Refrigerator className="w-3 h-3 mr-1" />
                            Mini Fridge
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <div>
                                <span className="text-2xl font-bold text-blue-600">{formatCurrency(roomType.price_per_night)}</span>
                                <span className="text-gray-500 text-sm">/night</span>
                              </div>
                              {/* Show non-refundable price if discount > 0 */}
                              {roomType.non_refundable_discount > 0 && (
                                <div className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 rounded">
                                  <span className="text-xs text-green-700 font-medium">Non-ref:</span>
                                  <span className="text-sm font-bold text-green-600">
                                    {formatCurrency(roomType.price_per_night * (1 - roomType.non_refundable_discount / 100))}/night
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatCurrency(roomType.total_price)} total for {roomType.nights} {roomType.nights === 1 ? 'night' : 'nights'}
                            </div>
                            {/* Show total savings for non-refundable */}
                            {roomType.non_refundable_discount > 0 && (
                              <div className="text-xs text-green-600 font-medium mt-1">
                                Save {formatCurrency(roomType.total_price * roomType.non_refundable_discount / 100)} with non-refundable rate
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {/* DISABLED: Add to Cart button - keeping code for future use
                            <Button 
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                addToCart(roomType);
                              }}
                              className="bg-white"
                            >
                              Add to Cart
                            </Button>
                            */}
                            <Button className="bg-blue-600 hover:bg-blue-700" data-testid={`view-room-type-${roomType.room_type_id}`}>
                              Book Now
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />
      </main>
    </div>
  );
};

export default LandingPage;